import type { CablePosition } from "../../findCableGaps";
import type { KarlCircuit } from "../../karlSteps";
import type { FindStrayDevices, StrayDevice, StrayGroup } from "./types";

/** Below this a "group" is too small for one member to be the odd one out. */
const MIN_GROUP_DEVICES = 4;

/** How much farther out than its own group's typical spacing a stray sits. */
const STRAY_RATIO = 3;

/**
 * A ratio alone flags a tight four-light group with one device in the next
 * room, which is a normal circuit. The floor is read off the plan itself —
 * six times how far a typical device sits from its own group's centre — so it
 * scales with the drawing without the phase having to know the scale.
 */
const PLAN_FLOOR_MULTIPLE = 6;

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

const distanceBetween = (a: CablePosition, b: CablePosition): number =>
  Math.hypot(a.x - b.x, a.y - b.y);

interface Measured {
  circuit: KarlCircuit;
  sourceName: string;
  placed: Array<{ destinationId: string; name: string; position: CablePosition }>;
  centre: CablePosition;
  typical: number;
}

const measure = (
  circuit: KarlCircuit,
  sourceName: string,
): Measured | null => {
  const placed = circuit.devices
    .filter(
      (device): device is typeof device & { position: CablePosition } =>
        device.position !== null,
    )
    .map((device) => ({
      destinationId: device.destinationId,
      name: device.name,
      position: device.position,
    }));
  if (placed.length < MIN_GROUP_DEVICES) return null;

  // Median, not mean: one device on the far side of the building must not drag
  // the centre towards itself and hide the very thing being looked for.
  const centre: CablePosition = {
    x: median(placed.map((device) => device.position.x)),
    y: median(placed.map((device) => device.position.y)),
  };
  return {
    circuit,
    sourceName,
    placed,
    centre,
    typical: median(
      placed.map((device) => distanceBetween(device.position, centre)),
    ),
  };
};

export const findStrayDevices: FindStrayDevices = ({ assignments }) => {
  const measured: Measured[] = [];
  for (const assignment of assignments) {
    for (const circuit of assignment.circuits) {
      const entry = measure(circuit, assignment.sourceName);
      if (entry) measured.push(entry);
    }
  }
  if (!measured.length) return [];

  const floor =
    PLAN_FLOOR_MULTIPLE * median(measured.map((entry) => entry.typical));
  // A plan whose groups have no spread at all says nothing about outliers.
  if (floor <= 0) return [];

  const groups: StrayGroup[] = [];
  for (const entry of measured) {
    const limit = Math.max(STRAY_RATIO * entry.typical, floor);
    const strays: StrayDevice[] = entry.placed
      .map((device) => ({
        destinationId: device.destinationId,
        name: device.name,
        position: device.position,
        distance: distanceBetween(device.position, entry.centre),
      }))
      .filter((device) => device.distance > limit)
      .sort((a, b) => b.distance - a.distance);
    if (!strays.length) continue;

    groups.push({
      circuitId: entry.circuit.circuitId,
      circuitKey: entry.circuit.circuitKey,
      sourceName: entry.sourceName,
      cableType: entry.circuit.cableType,
      deviceCount: entry.circuit.devices.length,
      typical: entry.typical,
      centre: entry.centre,
      strays,
      positions: entry.placed.map((device) => device.position),
    });
  }

  return groups.sort(
    (a, b) =>
      b.strays.length - a.strays.length ||
      b.strays[0].distance - a.strays[0].distance ||
      a.circuitId.localeCompare(b.circuitId),
  );
};
